import { onAnimationEnd } from '@theme/utilities';

/**
 * Mobile header drawer — slide layout.
 *
 * Open/close timing is managed in JS so transitions are stable:
 *   - OPEN: set `[open]` synchronously so the element renders off-screen.
 *     Force a layout, then in the next animation frame add `.is-open`,
 *     which is what CSS transitions to (translateX from -100% to 0).
 *   - CLOSE: remove `.is-open` so CSS transitions back to -100%, wait for
 *     the transition to finish, then remove `[open]`.
 *
 * Submenus (one nested <details> per parent link) follow the exact same pattern.
 */
class HeaderDrawerMobile extends HTMLElement {
  /** @type {HTMLDetailsElement | null} */
  _details = null;

  connectedCallback() {
    this._details = /** @type {HTMLDetailsElement | null} */ (
      this.querySelector('details.menu-drawer-mobile')
    );
    if (!this._details) return;

    if (this._details.hasAttribute('open')) this._details.classList.add('is-open');

    this.addEventListener('click', this._onClick);
    this.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('keydown', this._onKey);
  }

  /** @param {MouseEvent} event */
  _onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Zurück button → close its submenu.
    const back = target.closest('[data-submenu-close]');
    if (back instanceof HTMLElement && this.contains(back)) {
      event.preventDefault();
      const parent = /** @type {HTMLDetailsElement | null} */ (
        back.closest('details.menu-drawer-mobile__submenu-details')
      );
      if (parent) this._close(parent, this._submenuPanel(parent));
      return;
    }

    // Summary click → handle toggle ourselves.
    const summary = target.closest('summary');
    if (!(summary instanceof HTMLElement) || !this.contains(summary)) return;

    const details = /** @type {HTMLDetailsElement | null} */ (summary.parentElement);
    if (!details) return;

    event.preventDefault();

    const panel = details === this._details ? this._mainPanel() : this._submenuPanel(details);
    if (!panel) return;

    if (details.classList.contains('is-open')) {
      this._close(details, panel);
    } else {
      this._open(details, panel);
    }
  };

  /** @param {KeyboardEvent} event */
  _onKey = (event) => {
    if (event.key !== 'Escape') return;
    const openSubmenus = /** @type {NodeListOf<HTMLDetailsElement>} */ (
      this.querySelectorAll('details.menu-drawer-mobile__submenu-details.is-open')
    );
    if (openSubmenus.length > 0) {
      const last = openSubmenus[openSubmenus.length - 1];
      if (last) this._close(last, this._submenuPanel(last));
      return;
    }
    if (this._details && this._details.classList.contains('is-open')) {
      this._close(this._details, this._mainPanel());
    }
  };

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} panel
   */
  _open(details, panel) {
    details.open = true;
    // Force layout so the closed state is committed before we add .is-open.
    // Without this, browsers can batch [open] + class addition into one frame
    // and the transition skips.
    // eslint-disable-next-line no-unused-expressions
    panel.offsetHeight;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        details.classList.add('is-open');
      });
    });
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement | null} panel
   */
  _close(details, panel) {
    details.classList.remove('is-open');

    // When closing the main drawer, collapse any open submenus too.
    if (details === this._details) {
      this.querySelectorAll('details.menu-drawer-mobile__submenu-details.is-open').forEach(
        /** @param {Element} d */ (d) => {
          const sd = /** @type {HTMLDetailsElement} */ (d);
          sd.classList.remove('is-open');
          const sp = this._submenuPanel(sd);
          if (sp) onAnimationEnd(sp, () => { sd.open = false; });
          else sd.open = false;
        }
      );
    }

    if (!panel) {
      details.open = false;
      return;
    }
    onAnimationEnd(panel, () => {
      if (!details.classList.contains('is-open')) details.open = false;
    });
  }

  _mainPanel() {
    return /** @type {HTMLElement | null} */ (
      this._details?.querySelector(':scope > .menu-drawer-mobile__inner') ?? null
    );
  }

  /** @param {HTMLDetailsElement} details */
  _submenuPanel(details) {
    return /** @type {HTMLElement | null} */ (
      details.querySelector(':scope > .menu-drawer-mobile__submenu')
    );
  }
}

if (!customElements.get('header-drawer-mobile')) {
  customElements.define('header-drawer-mobile', HeaderDrawerMobile);
}
